import * as React from "react";
import {RefObject, useCallback, useEffect, useRef, useState} from "react";
import {Contract} from "src/domain/model/contract";
import styled from "styled-components";
import {pageTopInfoConfig} from "../../../../../config/language/ja-JP/presentation/page_top_info_text";
import {
    ConstBorderRound,
    ConstBreakPoint,
    ConstColor,
    ConstShadow,
} from "../../../../../config/styles_const/styles_const";
import {MessageEmpty} from "../../../../../config/view_const/message";
import {OuterURL} from "../../../../../config/view_const/outer_url";
import {AppNotificationType} from "../../../../../domain/model/app_notification_message";
import {MessageRoom} from "../../../../../domain/model/message_room";
import {createFileUsecase, IFileUseCase,} from "../../../../../domain/usecase/file_usecase";
import * as Validator from "../../../../../util/validation";
import HeadlineSmall from "../../../../common/atom/text/headline_small";
import Paragraph from "../../../../common/atom/text/paragraph";
import MessageCard from "../../../../common/molecular/card/message_card";
import MessageRoomProjectInfo from "../../../../common/organism/message_room/message_room_project_info";
import PageTopInfo from "../../../../common/organism/page_top_info";
import Progress from "../../../../common/organism/progress";
import {IAppNotificationMessageDispatcher} from "../../../../dispatcher/app_notification_message_dispatcher";
import {ICustomerMessageRoomDispatcher} from "../../../../dispatcher/customer_message_room_dispatcher";
import {AddAppNotificationRequest} from "../../../../request/app_notification_message_request";
import {
    CreateMessageRoomByCustomerRequest,
    SendMessageByCustomerRequest,
} from "../../../../request/message_room_request";
import {CustomerMessageRoomState} from "../../../../store/customer_message_room_state";
import {TMessageSignature} from "../../../../type/common";
import CustomerMessageRoomForm from "./customer_message_room_form";

const fileUseCase: IFileUseCase = createFileUsecase();
const convertGStoHTTPS = (gsURL: string): Promise<string> => {
    return fileUseCase.convertGStoHTTPS(gsURL);
};

const usePreviousMessageRoomCount = (value: number) => {
    const ref = useRef<number>(0);
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
};

const usePreviousIsLoading = (isLoading: boolean) => {
    const ref = useRef<boolean>(false);
    useEffect(() => {
        ref.current = isLoading;
    });
    return ref.current;
};

interface IProps {
    messageRoomState: CustomerMessageRoomState;
    messageRoomDispatcher: ICustomerMessageRoomDispatcher;
    notificationDispatcher: IAppNotificationMessageDispatcher;
    initialMessageRoom: MessageRoom | null;
    contracts: Contract[];
    match: any;
}

interface ICustomerMessageRoomFormParamsType {
    textareaRef: RefObject<HTMLTextAreaElement>;
    onSubmit: (values: any) => void;
}

const CustomerMessageRoomMain: React.FC<IProps> = (props) => {
    const [messageListRefIsInit, setMessageListRefIsInit] =
        useState<boolean>(false);
    const [messageListRef, setMessageListRef] = useState<HTMLDivElement | null>(
        null
    );
    const [isInit, setIsInit] = useState<boolean>(true);
    const [isOpponentInfo, setisOpponentInfo] = useState<boolean>(false);

    const messageListHandleRef = useCallback((node) => {
        if (node !== null) {
            setMessageListRef(node);
        }
    }, []);

    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    const handleClickFile = (event: React.MouseEvent<HTMLElement>): boolean => {
        const a = event.currentTarget as HTMLAnchorElement;

        if (/^gs:/.test(a.href)) {
            event.preventDefault();
            convertGStoHTTPS(a.href).then((url: string) => {
                a.href = url;
                a.click();
            });
            return false;
        }

        return false;
    };
    const {
        messageRoomState,
        messageRoomDispatcher,
        notificationDispatcher,
        initialMessageRoom,
        contracts,
        match,
    } = props;
    const {
        messageRoom,
        project,
        messages,
        ownSignature,
        opponentSignature,
        isLoading,
    } = messageRoomState;

    const prevMessageCount = usePreviousMessageRoomCount(messages.length);
    const prevIsLoading = usePreviousIsLoading(isLoading);

    const initialProjectId = initialMessageRoom
        ? initialMessageRoom.projectID
        : "";
    const initialOpponentId = initialMessageRoom
        ? initialMessageRoom.companyID
        : "";

    const projectId: string = match.params.projectId
        ? match.params.projectId
        : initialProjectId;
    const opponentId: string = match.params.companyId
        ? match.params.companyId
        : initialOpponentId;

    const handleOpponentInfoToggle = useCallback(async (e: any) => {
        setisOpponentInfo((prevState) => !prevState);
    }, []);

    const handleSubmit = (values: any) => {
        const message = values.message ? values.message : "";

        // 成約済みの場合は validate なし
        let isContract: boolean = false;

        contracts.map((contract) => {
            if (
                contract &&
                contract.projectID === projectId &&
                contract.companyID === opponentId
            ) {
                isContract = true;
            }
        });

        // email phoneNumber が含まれていた場合
        const errorMessage = Validator.emailAndPhoneNumberInText(message);
        if (errorMessage && !isContract) {
            const item: AddAppNotificationRequest = new AddAppNotificationRequest();
            item.type = AppNotificationType.WARN;
            item.message = errorMessage;
            notificationDispatcher.add(item);
            return;
        }

        const req: SendMessageByCustomerRequest =
            new SendMessageByCustomerRequest();
        req.text = message;
        req.projectId = projectId;
        req.opponentId = opponentId;
        req.uploadFile = values.fileURLFileObject ? values.fileURLFileObject : null;
        messageRoomDispatcher.sendMessageRoom(req);
    };

    const customerMessageRoomFormParamsType: ICustomerMessageRoomFormParamsType =
        {
            textareaRef,
            onSubmit: handleSubmit,
        };

    useEffect(() => {
        if (projectId && opponentId) {
            const req: CreateMessageRoomByCustomerRequest =
                new CreateMessageRoomByCustomerRequest();
            req.projectId = projectId;
            req.opponentId = opponentId;
            messageRoomDispatcher.createMessageRoom(req);

            setMessageListRefIsInit(true);
            return () => {
                setMessageListRefIsInit(false);
                setIsInit(true);
                messageRoomDispatcher.destroyMessageRoom();
            };
        }

        setIsInit(false);

        return undefined;
    }, [projectId, opponentId]);

    useEffect(() => {
        if (messageListRefIsInit && messageListRef) {
            messageListRef.scrollIntoView(false);
        }
    }, [messageListRefIsInit, messageListRef]);

    useEffect(() => {
        // メッセージ送信後の最新メッセージfocus　&　メッセージボックスfocus
        // 条件 messageListRefとtextFieldRefが存在し props.state.messagesの変化があるとき
        if (
            messageListRef &&
            textareaRef.current &&
            prevMessageCount !== messages.length
        ) {
            messageListRef.scrollIntoView(false);
            // @ts-ignore
            textareaRef.current.focus();
        }
    }, [messageListRef, textareaRef.current, messages.length, prevMessageCount]);

    useEffect(() => {
        // const isInitFinished: boolean = this.state.isInit && !this.props.state.isLoading && prevProps.state.isLoading;
        const isInitFinished: boolean = isInit && !isLoading && prevIsLoading;
        if (isInitFinished) {
            setIsInit(false);
        }
    }, [isLoading]);

    if (isInit) {
        return <Progress canAnimation={false}/>;
    }

    if (
        !messageRoom ||
        !messages ||
        !ownSignature ||
        !opponentSignature ||
        !project
    ) {
        return <div/>;
    }

    return (
        <React.Fragment>
            <MessageRoomWrapper>
                <MessageCardsWrapper>
                    <PageTopInfo
                        information={pageTopInfoConfig.customerMessage}
                        linkURL={OuterURL.customerMessageFaqURL}
                    />

                    <ProjectMessage>
                        <ProjectMessageThumbnail src={project.getThumbnailURL()}/>
                        <ProjectMessageText>
                            <Paragraph bold={true}>{project.name}</Paragraph>
                            <Paragraph>{project.description}</Paragraph>
                        </ProjectMessageText>
                    </ProjectMessage>

                    <OpponentInfoWrapper>
                        <OpponentToggleWrapper>
                            <Paragraph bold={true}>案件情報</Paragraph>
                            <ToggleIcon icon={"'\f1de'"} onClick={handleOpponentInfoToggle}/>
                        </OpponentToggleWrapper>
                        {isOpponentInfo && (
                            <RightWrapper>
                                <MessageRoomProjectInfo
                                    project={project}
                                    companyIcon={opponentSignature.iconURL}
                                    companyName={opponentSignature.name}
                                    projectURL={`/customer/project/${project.id}`}
                                    contractURL={`/customer/contract/registration/${project.companyID}/${project.id}/${messageRoom.customerID}`}
                                    customerId={messageRoom.customerID}
                                    messageRoom={messageRoom!}
                                />
                            </RightWrapper>
                        )}
                    </OpponentInfoWrapper>

                    {messages.length === 0 && (
                        <Paragraph
                            style={{textAlign: "center", lineHeight: "calc(100vh - 71px)"}}
                        >
                            {MessageEmpty.messageForCustomer}
                        </Paragraph>
                    )}

                    {messages
                        .slice()
                        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
                        .map((message, index) => {
                            const messageSignature: TMessageSignature =
                                message.fromID === ownSignature.id
                                    ? ownSignature
                                    : opponentSignature;
                            const isOwnMessage: boolean = !message.fromCompany;

                            return (
                                <MessageCard
                                    key={message.id}
                                    isOwnMessage={isOwnMessage}
                                    messageSignatureIconURL={messageSignature.iconURL}
                                    messageSignatureName={messageSignature.name}
                                    messageCreateTimeString={message.getCreatedAtString()}
                                    messageText={message.text}
                                    messageFileUrl={message.gsFileURL}
                                    handleClickFile={handleClickFile}
                                />
                            );
                        })}

                    <MessageAnnotation>
                        {messages.length > 0 && !messages[messages.length - 1].fromCompany && (
                            <MessageAnnotationMessage>
                                <p style={{"color": "white"}}>相手の返信待ち</p>
                            </MessageAnnotationMessage>
                        )}
                        {messages.length > 0 && messages[messages.length - 1].fromCompany && (
                            <MessageAnnotationMessage2>
                                <p style={{"color": "white"}}>返信を行ってください、または成約済みの場合右側のボタンから成約報告を行ってください</p>
                            </MessageAnnotationMessage2>
                        )}
                    </MessageAnnotation>

                    <LastMessage tabIndex={-1} ref={messageListHandleRef}/>
                </MessageCardsWrapper>

                <CustomerMessageRoomForm {...customerMessageRoomFormParamsType} />
            </MessageRoomWrapper>
        </React.Fragment>
    );
};

export default CustomerMessageRoomMain;

const MessageRoomWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
`;

const MessageCardsWrapper = styled.div`
    flex: 1;
    padding-left: 20px;
    padding-right: 20px;
    @media screen and (min-width: ${ConstBreakPoint.Default}) {
        padding-left: 10px;
        padding-right: 10px;
    }
    @media screen and (min-width: ${ConstBreakPoint.PcDeskTop}) {
        padding-left: 30px;
        padding-right: 30px;
    }
`;

const OpponentInfoWrapper = styled.div`
    position: sticky;
    top: 60px;
    background-color: ${ConstColor.White};

    padding: 12px 20px;
    margin-bottom: 20px;
    border-radius: ${ConstBorderRound.CornerRoundAll};
    box-shadow: ${ConstShadow.DropShadow};
    @media screen and (min-width: ${ConstBreakPoint.Default}) {
        top: 0px;
    }
    @media screen and (min-width: ${ConstBreakPoint.PcDeskTop}) {
        display: none;
    }
`;

const ToggleIcon = styled(HeadlineSmall)`
    :hover {
        cursor: pointer;
    }
`;

const OpponentToggleWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const RightWrapper = styled.div`
    display: block;
    margin-top: 25px;
    @media screen and (min-width: ${ConstBreakPoint.Default}) {
        display: flex;
    }
`;

const LastMessage = styled.div`
    height: 100px;
`;

const ProjectMessage = styled.div`
    width: 100%;
    height: 100px;
    display: flex;
    border: solid 1px #D8D8D8;
    border-radius: 4px;
`;

const ProjectMessageThumbnail = styled.img`
    width: 240px;
    height: 100px;
    object-fit: cover;
`;

const ProjectMessageText = styled.div`
    width: 100%;
    height: 100px;
    padding: 10px;
`;

const MessageAnnotation = styled.div`
    width: 100%;
    height: 50px;
    display: flex;
    justify-content: center;
`;

const MessageAnnotationMessage = styled.div`
    width: 530px;
    height: 50px;
    border-radius: 25px;
    background-color: #00D58E;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const MessageAnnotationMessage2 = styled.div`
    width: 720px;
    height: 50px;
    border-radius: 25px;
    background-color: #CE4141;
    display: flex;
    justify-content: center;
    align-items: center;
`;



// WEBPACK FOOTER //
// ./src/presentation/component/customer/message_rooms/main/customer_message_rooms_main.tsx